import type { FocusedWindow } from '@highlight/common'
import { waitForFocusedWindow } from '@renderer/hooks/use-focused-window'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer'
import type { StagedAction } from '@renderer/overlay/Sidepanel/hooks/staged-actions/types'

/** The link the gateway builds onto a planner v2 action on every read; undefined when it has none. */
const actionDeepLink = (action: Pick<StagedAction, 'metadata'>): string | undefined =>
  action.metadata?.deepLink || undefined

const OPEN_LABELS: Record<string, string> = {
  gmail: 'Open in Gmail',
  google_calendar: 'Open in Calendar',
  slack: 'Open in Slack',
}

/** The action's own app, opened on the gateway's deep link (#7808); Slack's can't carry the message, so it's pasted in. */
export function openInAppLink(
  action: StagedAction,
  readDraft: () => string | undefined,
): { label: string; open: () => void } | null {
  const link = actionDeepLink(action)
  const label = OPEN_LABELS[action.toolSet ?? '']
  if (!link || !label) return null
  if (action.toolSet !== 'slack') return { label, open: () => window.open(link, '_blank') }
  return {
    label,
    open: () => {
      // A second click while the first still waits on its draft would paste it twice
      if (isOpeningSlack) return
      isOpeningSlack = true
      openSlackWithDraft(link, readDraft, slackRecipient(action))
        .catch(console.error)
        .finally(() => {
          isOpeningSlack = false
        })
    },
  }
}

let isOpeningSlack = false

/** How long Slack gets to come forward, then to switch conversations, then to settle before the paste. */
const SLACK_FOCUS_TIMEOUT_MS = 5_000
// Slack switches in place within a few hundred ms; the title is only re-read once a second, so this caps a late signal
const SLACK_SWITCH_TIMEOUT_MS = 500
const SLACK_SETTLE_MS = 300
// The title poll can lag 2.5s with the sidepanel closed; past this, the draft only goes to the clipboard
const SLACK_RECIPIENT_TIMEOUT_MS = 3_000
/** How long to wait for the focus snapshot when reading which app is in front at the click. */
const FOCUS_READ_TIMEOUT_MS = 200
/** How long a click that beat the planner's draft waits for it. */
const SLACK_DRAFT_TIMEOUT_MS = 20_000
const DRAFT_POLL_MS = 500

/** Slack links can't carry a message, so the draft is pasted once Slack has switched to the DM; it also stays on the clipboard. */
async function openSlackWithDraft(url: string, readDraft: () => string | undefined, recipient?: string): Promise<void> {
  // Read what's in front before the link changes it, but open within the click's gesture
  const frontAtClickRead = waitForFocusedWindow(() => true, FOCUS_READ_TIMEOUT_MS)
  window.open(url, '_blank')
  const frontAtClick = await frontAtClickRead
  const draftAtClick = readDraft()
  // Slack's title leads with the open conversation's name, so a match means the link has nowhere to go
  if (frontAtClick && isSlack(frontAtClick) && recipient && titleNames(frontAtClick.windowTitle, recipient)) {
    const message = draftAtClick ?? (await waitForDraft(readDraft))
    // Re-checked at paste time: the paste goes to whatever has focus then, and the snapshot can be a second old
    if (message) await pasteIntoSlack(message, frontAtClick.pid, recipient)
    return
  }
  const message = draftAtClick ?? (await waitForDraft(readDraft))
  if (!message) return
  const slack = await waitForFocusedWindow(isSlack, SLACK_FOCUS_TIMEOUT_MS)
  if (!slack) {
    await OverlayIpcRenderer.writeTextToClipboard(message)
    return
  }
  // A Slack brought forward by the link is already on the DM; one already in front switches in place, which its title shows
  const switchesInPlace = !!frontAtClick && isSlack(frontAtClick)
  if (switchesInPlace) {
    await waitForFocusedWindow(
      (focused) =>
        isSlack(focused) &&
        (recipient ? titleNames(focused.windowTitle, recipient) : focused.windowTitle !== frontAtClick.windowTitle),
      recipient ? SLACK_RECIPIENT_TIMEOUT_MS : SLACK_SWITCH_TIMEOUT_MS,
    )
  }
  await new Promise((resolve) => window.setTimeout(resolve, SLACK_SETTLE_MS))
  // Pasting into a Slack still on another conversation would send the draft to the wrong person
  await pasteIntoSlack(message, slack.pid, switchesInPlace || !draftAtClick ? recipient : undefined)
}

/** macOS names the app "Slack"; Windows reports its executable. */
function isSlack(focused: FocusedWindow): boolean {
  return /^slack(\.exe)?$/i.test(focused.appName)
}

/** How long the paste takes to put the user's previous clipboard back (OverlayIpc's typeText restores it at 200ms). */
const CLIPBOARD_RESTORE_MS = 300

/** Pastes the draft and leaves it on the clipboard; with `recipient`, a late draft only pastes if Slack is still on their conversation. */
async function pasteIntoSlack(message: string, pid: number, recipient?: string): Promise<void> {
  const focused = recipient ? await waitForFocusedWindow(() => true, FOCUS_READ_TIMEOUT_MS) : null
  if (!recipient || (focused && isSlack(focused) && titleNames(focused.windowTitle, recipient))) {
    await OverlayIpcRenderer.typeText({ text: message, pid, method: 'paste' })
    await new Promise((resolve) => window.setTimeout(resolve, CLIPBOARD_RESTORE_MS))
  }
  // Slack routes a paste to the message box even with focus on the list, but keep the draft on hand in case it didn't
  await OverlayIpcRenderer.writeTextToClipboard(message)
}

/** Who a Slack action is addressed to, as its view names them: the person, or the channel without its `#`. */
function slackRecipient(action: StagedAction): string | undefined {
  const tag = action.view?.tags.find((candidate) => candidate.key === 'User' || candidate.key === 'Channel')
  return tag?.value.replace(/^#/, '').trim() || undefined
}

/** Whether a Slack window title ("Chris Sherman (DM) - Workspace - Slack") is on the recipient's conversation; "Chris" names "Chris Sherman" but no group DM ("Chris Sherman, Lin"). */
function titleNames(title: string, recipient: string): boolean {
  const conversation = title
    .split(' - ')[0]
    .replace(/\s*\((DM|Channel)\)\s*$/i, '')
    .replace(/^#/, '')
    .trim()
    .toLowerCase()
  const name = recipient.toLowerCase()
  if (conversation.includes(',') && !name.includes(',')) return false
  return conversation === name || conversation.startsWith(`${name} `)
}

async function waitForDraft(readDraft: () => string | undefined): Promise<string | undefined> {
  const deadline = Date.now() + SLACK_DRAFT_TIMEOUT_MS
  while (Date.now() < deadline) {
    await new Promise((resolve) => window.setTimeout(resolve, DRAFT_POLL_MS))
    const draft = readDraft()
    if (draft) return draft
  }
  return undefined
}
